import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Empty, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router';
import { useEstadoDeLista } from '@titularis/ui';
import type { ExamList, ExamModule, ExamStatus } from '@titularis/api-client';
import { api } from '../../api';
import {
  MODULO_LABEL,
  MODULOS,
  STATUS_PROVA,
  STATUS_PROVA_COR,
  STATUS_PROVA_LABEL,
} from '../../dominio';

/**
 * Lista de provas. É o ponto de partida do cadastro: questão avulsa é
 * bloqueada, então tudo começa por uma prova existente.
 */
type ProvaResumo = ExamList['items'][number];

export function ProvasPage() {
  const navigate = useNavigate();
  const { ler, filtrar, irParaPagina, busca } = useEstadoDeLista();

  // Filtros e página na URL: a tela sobrevive a sair, voltar e recarregar.
  const termo = ler.texto('termo') ?? '';
  const modules = ler.lista('modules').filter((valor): valor is ExamModule =>
    MODULOS.includes(valor as ExamModule),
  );
  const statuses = ler.lista('statuses').filter((valor): valor is ExamStatus =>
    STATUS_PROVA.includes(valor as ExamStatus),
  );
  const page = ler.pagina();

  const provas = useQuery({
    queryKey: ['exams', { termo, modules, statuses, page }],
    queryFn: () =>
      api.listExams({ termo: termo || undefined, modules, statuses, page, pageSize: 20 }),
  });

  const vazioSemFiltro =
    !termo && modules.length === 0 && statuses.length === 0 && provas.data?.total === 0;

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
        <div>
          <Typography.Title level={3} style={{ marginBottom: 4 }}>
            Provas
          </Typography.Title>
          <Typography.Text type="secondary">
            Toda questão pertence a uma prova. Cadastre a prova antes de cadastrar as questões.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => void navigate('/provas/nova')}>
          Cadastrar prova
        </Button>
      </Space>

      {provas.error ? (
        <Alert type="error" showIcon title={(provas.error as Error).message} />
      ) : null}

      <Card size="small">
        <Space wrap style={{ marginBottom: 16 }}>
          <Input.Search
            allowClear
            placeholder="Buscar pelo nome"
            style={{ width: 260 }}
            defaultValue={termo}
            onSearch={(valor) => filtrar({ termo: valor })}
          />
          <Select<ExamModule[]>
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="Módulo"
            style={{ width: 230 }}
            value={modules}
            onChange={(valor) => filtrar({ modules: valor })}
            options={MODULOS.map((valor) => ({ value: valor, label: MODULO_LABEL[valor] }))}
          />
          <Select<ExamStatus[]>
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="Situação"
            style={{ width: 200 }}
            value={statuses}
            onChange={(valor) => filtrar({ statuses: valor })}
            options={STATUS_PROVA.map((valor) => ({
              value: valor,
              label: STATUS_PROVA_LABEL[valor],
            }))}
          />
        </Space>

        <Table<ProvaResumo>
          rowKey="id"
          loading={provas.isPending}
          dataSource={provas.data?.items ?? []}
          /*
            Largura em número, e não `true`: com as colunas de distribuição e
            downloads, o `true` deixa a tabela do tamanho do contêiner e é a
            última coluna que sai da tela sem avisar.
          */
          scroll={{ x: 1120 }}
          locale={{
            emptyText: vazioSemFiltro ? (
              <Empty
                description="Nenhuma prova cadastrada ainda."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" onClick={() => void navigate('/provas/nova')}>
                  Cadastrar a primeira prova
                </Button>
              </Empty>
            ) : (
              <Empty description="Nenhuma prova com esses filtros." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ),
          }}
          pagination={{
            current: page,
            pageSize: provas.data?.pageSize ?? 20,
            total: provas.data?.total ?? 0,
            onChange: (pagina: number) => irParaPagina(pagina),
            hideOnSinglePage: true,
            showSizeChanger: false,
          }}
          onRow={(prova) => ({
            style: { cursor: 'pointer' },
            // A prova leva de onde veio, para o voltar devolver esta lista.
            onClick: () => void navigate(`/provas/${prova.id}`, { state: { origem: busca } }),
          })}
          columns={[
            { title: 'Prova', dataIndex: 'name' },
            {
              title: 'Módulo',
              dataIndex: 'module',
              width: 190,
              render: (valor: ExamModule) => MODULO_LABEL[valor],
            },
            { title: 'Ano', dataIndex: 'year', width: 90 },
            {
              title: 'Situação',
              dataIndex: 'status',
              width: 130,
              render: (valor: ExamStatus) => (
                <Tag color={STATUS_PROVA_COR[valor]}>{STATUS_PROVA_LABEL[valor]}</Tag>
              ),
            },
            {
              /*
                Chave vizinha da situação, e as duas juntas na mesma linha: são
                perguntas diferentes - "as questões estão no app?" e "o PDF está
                na web aberta?" - e é olhando as duas colunas lado a lado que se
                percebe a prova cujo arquivo ninguém liberou.
              */
              title: 'Distribuição',
              dataIndex: 'filesPublic',
              width: 130,
              render: (publica: boolean) =>
                publica ? (
                  <Tag color="green">Pública</Tag>
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                ),
            },
            {
              /*
                A coluna que responde à pergunta pela qual o contador existe:
                **quais provas as pessoas querem**. Ordenável porque a resposta
                é uma comparação, e comparação sem ordem é uma lista de números.
              */
              title: 'Downloads',
              dataIndex: 'downloads',
              width: 120,
              align: 'right',
              sorter: (a: ProvaResumo, b: ProvaResumo) => a.downloads - b.downloads,
              render: (total: number) =>
                total > 0 ? (
                  total.toLocaleString('pt-BR')
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                ),
            },
            {
              title: 'Cadastrada por',
              dataIndex: 'createdByName',
              width: 170,
              // Autor excluído aparece como "Conta excluída"; prova antiga sem
              // autor registrado, como "—".
              render: (nome: string | null, prova) =>
                prova.createdBy ? (
                  (nome ?? 'Conta excluída')
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                ),
            },
          ]}
        />
      </Card>
    </Space>
  );
}
